import { useState, useEffect } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bell, Loader2, HelpCircle, PlayCircle, Send, AlertTriangle, Wrench } from 'lucide-react';
import { TRIGGER_ONBOARDING_EVENT } from './NotificationPermissionModal';
import { getInitDiagnostics, isIOS as detectIOS, isInstalledPWA as detectPWA } from '@/lib/oneSignalService';

// Detect Mac/Safari
function isMac(): boolean {
  return /Mac|Macintosh/.test(navigator.userAgent) && !('ontouchend' in document);
}

function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function NotificationDiagnostics() {
  const {
    isSupported,
    isEnabled,
    isInitialized,
    needsIOSInstall,
    isDenied,
    isLoading,
    hasDbSubscription,
    isDesynchronized,
    toggle,
    enable,
    sendTestNotification,
    repair,
  } = useNotifications();
  const [showRetryHint, setShowRetryHint] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [diagnostics, setDiagnostics] = useState<{ duration: number | null; error: string | null }>({ duration: null, error: null });

  // Load diagnostics when loading state changes
  useEffect(() => {
    if (!isLoading) {
      const diag = getInitDiagnostics();
      setDiagnostics({
        duration: diag.duration,
        error: diag.error,
      });
    }
  }, [isLoading]);

  const handleToggle = async (enabled: boolean) => {
    const success = await toggle(enabled);
    if (success) {
      toast.success(enabled ? 'Notifications activées' : 'Notifications désactivées');
      setShowRetryHint(false);
    } else if (enabled) {
      if (isDenied) {
        toast.error('Notifications bloquées par le navigateur');
        setShowRetryHint(true);
      } else if (needsIOSInstall) {
        toast.error('Installez l\'app depuis Safari pour activer les notifications');
      } else {
        toast.error('Impossible d\'activer les notifications');
        setShowRetryHint(true);
      }
    }
  };

  const handleRetryActivation = async () => {
    const success = await enable();
    if (success) {
      toast.success('Notifications activées !');
      setShowRetryHint(false);
    }
  };

  const handleTestOnboarding = () => {
    window.dispatchEvent(new CustomEvent(TRIGGER_ONBOARDING_EVENT));
  };

  const handleRepair = async () => {
    if (isRepairing) return;
    
    setIsRepairing(true);
    try {
      const success = await repair();
      if (success) {
        toast.success('Notifications réparées !');
      } else {
        toast.error('Échec de la réparation. Réessayez.');
      }
    } catch {
      toast.error('Erreur lors de la réparation.');
    } finally {
      setIsRepairing(false);
    }
  };

  const handleSendTestNotification = async () => {
    if (isSendingTest) return;
    
    setIsSendingTest(true);
    try {
      const success = await sendTestNotification();
      if (success) {
        toast.success('Notification envoyée ! Vérifiez vos notifications.');
      } else {
        toast.error('Échec de l\'envoi de la notification test.');
      }
    } catch {
      toast.error('Erreur lors de l\'envoi de la notification.');
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm">Notifications</span>
        </div>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Bell className="w-5 h-5" />
          <div>
            <p className="text-sm font-medium">Notifications non disponibles</p>
            <p className="text-xs">Votre navigateur ne supporte pas les notifications push.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main toggle */}
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
            <Bell className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <p className="text-sm font-medium">Notifications push</p>
            <p className="text-xs text-muted-foreground">
              {isEnabled 
                ? 'Activées - vous recevez les alertes'
                : 'Désactivées'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isLoading || isDenied || needsIOSInstall}
        />
      </div>

      {/* OneSignal status with diagnostics */}
      <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">OneSignal</span>
          <span className={`font-mono ${isInitialized ? 'text-green-600' : diagnostics.error ? 'text-red-600' : 'text-amber-600'}`}>
            {isInitialized ? '✓ Connecté' : diagnostics.error ? '✗ Erreur' : '⏳ Initialisation...'}
          </span>
        </div>
        
        {/* Device info */}
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Appareil</span>
          <span className="font-mono">
            {detectIOS() ? (detectPWA() ? 'iOS PWA' : 'iOS Safari') : 'Web'}
          </span>
        </div>
        
        {/* DB Subscription status */}
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Subscription DB</span>
          <span className={`font-mono ${hasDbSubscription ? 'text-green-600' : 'text-amber-600'}`}>
            {hasDbSubscription ? '✓ OK' : '✗ Absente'}
          </span>
        </div>
        
        {/* Init duration */}
        {diagnostics.duration !== null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Temps init</span>
            <span className="font-mono">{diagnostics.duration}ms</span>
          </div>
        )}
        
        {/* Error display */}
        {diagnostics.error && (
          <div className="flex items-start gap-2 p-2 bg-red-500/10 rounded text-red-600">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="text-xs">{diagnostics.error}</span>
          </div>
        )}
      </div>

      {/* Desynchronization warning with repair button */}
      {isDesynchronized && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-600 mb-1">Notifications désynchronisées</p>
              <p className="text-xs text-red-600/80 mb-3">
                Le profil indique que les notifications sont activées, mais aucune subscription n'existe. 
                Cliquez sur "Réparer" pour corriger ce problème.
              </p>
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={handleRepair}
                disabled={isRepairing}
                className="w-full"
              >
                {isRepairing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wrench className="w-4 h-4 mr-2" />
                )}
                Réparer mes notifications
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send test notification button - only when enabled */}
      {isEnabled && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendTestNotification}
          disabled={isSendingTest}
          className="w-full"
        >
          {isSendingTest ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Envoyer une notification test
        </Button>
      )}

      {/* iOS install hint */}
      {needsIOSInstall && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm font-medium text-blue-600 mb-1">📱 Installation requise</p>
          <p className="text-xs text-blue-600/80">
            Pour activer les notifications sur iPhone/iPad, installez d'abord l'application :
          </p>
          <ol className="text-xs text-blue-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Appuyez sur <strong>Partager</strong> (📤)</li>
            <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
            <li>Ouvrez l'app depuis l'écran d'accueil</li>
          </ol>
        </div>
      )}

      {/* Permission denied hint */}
      {isDenied && !needsIOSInstall && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm font-medium text-amber-600 mb-1">🔒 Notifications bloquées</p>
          <p className="text-xs text-amber-600/80">
            Les notifications sont bloquées par votre navigateur. Pour les réactiver :
          </p>
          <ol className="text-xs text-amber-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Cliquez sur l'icône 🔒 dans la barre d'adresse</li>
            <li>Trouvez "Notifications" et changez en "Autoriser"</li>
            <li>Rechargez la page</li>
          </ol>
        </div>
      )}

      {/* Mac/Safari specific hint */}
      {isMac() && isSafari() && !isEnabled && !isDenied && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <p className="text-sm font-medium text-purple-600 mb-1">🍎 Safari sur Mac</p>
          <p className="text-xs text-purple-600/80">
            Safari sur Mac supporte les notifications web. Assurez-vous que :
          </p>
          <ol className="text-xs text-purple-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>macOS 13+ (Ventura) et Safari 16+</li>
            <li>Notifications autorisées dans Préférences Système → Notifications → Safari</li>
            <li>Le site a la permission d'envoyer des notifications</li>
          </ol>
        </div>
      )}

      {/* Retry activation hint - shown when activation failed but not denied */}
      {showRetryHint && !isDenied && !needsIOSInstall && !isEnabled && (
        <div className="p-3 bg-muted/50 border border-border rounded-lg">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">L'activation n'a pas fonctionné ?</p>
              <p className="text-xs text-muted-foreground mb-3">
                La popup du navigateur a peut-être été fermée trop vite.
              </p>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleRetryActivation}
                className="w-full"
              >
                Réessayer l'activation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test onboarding button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleTestOnboarding}
        className="w-full text-muted-foreground hover:text-foreground"
      >
        <PlayCircle className="w-4 h-4 mr-2" />
        Tester l'onboarding
      </Button>
    </div>
  );
}
